// Turn the backend's NavigateIntent into a grounded, CONFIRMABLE navigation proposal. GROUNDING happens
// against the live, permission-scoped destination catalog (navTargets) — a page the user can't reach, or a
// phrase that matches nothing, becomes a clarification, never a silent jump. Otto always CONFIRMS before
// navigating (navigation leaves the current page), and only ever offers pages the user can actually open.
import { groundDestination } from "./navCatalog";
import { pageReference } from "../planner/pageManifest";
import { capPageChoices } from "../planner/pageRanking";

// Join labels conversationally: "A", "A or B", "A, B, or C".
const joinLabels = labels => {
    const l = labels.filter(Boolean);
    if (l.length <= 1) return l[0] || "";
    if (l.length === 2) return `${l[0]} or ${l[1]}`;
    return `${l.slice(0, -1).join(", ")}, or ${l[l.length - 1]}`;
};

const clarify = (base, message, candidates) => ({
    ...base,
    navigate: true,
    needsClarification: true,
    target: null,
    options: (candidates || []).map(t => t.label),
    message
});

// interpretResponse = { request_id, session_id, intent_kind: "navigate", navigate: NavigateIntent };
// ctx = { utterance, navTargets }. The model only CAPTURES the destination phrase; WHICH real page that is
// (and whether it's reachable) is resolved HERE against navTargets, so navigation is always grounded.
export const buildNavProposal = (interpretResponse, ctx = {}) => {
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
    const navTargets = ctx.navTargets || [];
    const intent = (interpretResponse && interpretResponse.navigate) || {};
    const phrase = (intent.destination_phrase || "").trim();

    const topLabels = navTargets.slice(0, 6).map(t => t.label);
    const placesLine = topLabels.length ? ` I can take you to ${joinLabels(topLabels)}.` : "";

    // The model couldn't tell WHERE the user wants to go (e.g. "take me somewhere").
    if (intent.needs_clarification || !phrase) {
        return clarify(base, `Where would you like to go?${placesLine}`, navTargets.slice(0, 6));
    }

    // Grounded WITH the current page: page names are not unique (the project hub and the asset hub both
    // have Regions/Sites/Buildings), so the page the user is on is what splits an otherwise permanent tie.
    const grounded = groundDestination(phrase, navTargets, { page: ctx.page, utterance: ctx.utterance });

    if (grounded.decision === "auto" && grounded.target) {
        return {
            ...base,
            navigate: true,
            needsClarification: false,
            target: grounded.target,
            options: [],
            // Friendlier headline than the button (which stays the short "Go to <label>"), so the two
            // don't read as a duplicate. A data-grid page reads as "the Recommendations Table View".
            message: `Sure — want me to take you to the ${pageReference(grounded.target)}?`
        };
    }

    if (grounded.decision === "clarify" && (grounded.candidates || []).length) {
        // Ranked candidates, CAPPED: past three buttons this stops being a clarification and becomes a
        // directory. What didn't fit is said out loud — presenting the top three as if they were the whole
        // set is the failure mode capping would otherwise introduce.
        const { shown, overflow } = capPageChoices(grounded.candidates);
        const tail = overflow.length ? " — or name another page." : "";
        return clarify(base, `Did you mean ${joinLabels(shown.map(t => t.label))}?${tail}`, shown);
    }

    // Nothing reachable matched the phrase.
    return clarify(base, `I couldn't find a “${phrase}” page you can open.${placesLine}`, navTargets.slice(0, 6));
};
